import * as React from "react";
import { useMemo, useState } from "react";

import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import CardGiftcardIcon from "@material-ui/icons/CardGiftcard";
import PeopleIcon from "@material-ui/icons/People";
import ShareIcon from "@material-ui/icons/Share";
import PollIcon from "@material-ui/icons/Poll";
import StoreIcon from "@material-ui/icons/Store";
import EmojiEventsIcon from "@material-ui/icons/EmojiEvents";
import SearchIcon from "@material-ui/icons/Search";

export type InflightDetailsPayload = {
    user_id?: number;
    details?: Record<string, { items?: Record<string, unknown>[] }>;
    meta?: {
        counts?: Record<string, number>;
        totals?: Record<string, number>;
    };
};

type SectionConfig = {
    key: string;
    label: string;
    totalKey: string;
    icon: React.ReactElement;
};

const SECTIONS: SectionConfig[] = [
    { key: "promo_code", label: "Promo Code", totalKey: "promo_code", icon: <LocalOfferIcon /> },
    { key: "gift_card", label: "Gift Card", totalKey: "gift_card", icon: <CardGiftcardIcon /> },
    { key: "affiliate", label: "Affiliate", totalKey: "affiliate", icon: <PeopleIcon /> },
    { key: "referral_v1", label: "Referral V1", totalKey: "referral_v1", icon: <ShareIcon /> },
    { key: "referral_v2", label: "Referral V2", totalKey: "referral_v2", icon: <ShareIcon /> },
    { key: "survey", label: "Survey", totalKey: "survey", icon: <PollIcon /> },
    { key: "vdx", label: "VDX", totalKey: "vdx", icon: <StoreIcon /> },
    { key: "prize_draw", label: "Prize Draw", totalKey: "prize_draw", icon: <EmojiEventsIcon /> },
];

const useStyles = makeStyles((theme) => ({
    root: {
        width: "100%",
        maxWidth: 960,
        margin: "0 auto",
        padding: theme.spacing(0, 2, 4),
        boxSizing: "border-box",
    },
    hero: {
        borderRadius: theme.spacing(2),
        padding: theme.spacing(2.5, 3),
        marginBottom: theme.spacing(2),
        background:
            "linear-gradient(135deg, rgba(218, 41, 114, 0.12), rgba(255, 255, 255, 0.98))",
        border: "1px solid rgba(218, 41, 114, 0.18)",
        boxShadow: "0 8px 28px rgba(218, 41, 114, 0.08)",
    },
    heroTop: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: theme.spacing(2),
    },
    heroAmount: {
        fontWeight: 800,
        fontSize: "2rem",
        color: theme.palette.primary.main,
        lineHeight: 1.1,
    },
    chipRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(1),
        marginTop: theme.spacing(1.5),
    },
    summaryChip: {
        fontWeight: 600,
    },
    accordion: {
        marginBottom: theme.spacing(1),
        borderRadius: `${theme.spacing(1.5)}px !important`,
        overflow: "hidden",
        border: "1px solid rgba(28, 30, 31, 0.08)",
        boxShadow: "none",
        "&:before": { display: "none" },
    },
    accordionSummary: {
        background: "linear-gradient(90deg, rgba(248, 246, 243, 0.9), #fff)",
        minHeight: 56,
    },
    sectionTitle: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        fontWeight: 700,
    },
    sectionMeta: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        marginLeft: "auto",
        marginRight: theme.spacing(1),
    },
    tableWrap: {
        overflowX: "auto",
        width: "100%",
    },
    table: {
        minWidth: 480,
    },
    headCell: {
        backgroundColor: "#f8f4f6",
        fontWeight: 700,
        fontSize: "0.78rem",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        whiteSpace: "nowrap",
    },
    bodyCell: {
        fontSize: "0.85rem",
        verticalAlign: "top",
    },
    amountCell: {
        fontWeight: 700,
        color: theme.palette.primary.main,
    },
    emptySection: {
        padding: theme.spacing(2),
        color: theme.palette.text.secondary,
        fontSize: "0.9rem",
    },
    emptyState: {
        textAlign: "center",
        padding: theme.spacing(6, 2),
        color: theme.palette.text.secondary,
    },
    errorBox: {
        padding: theme.spacing(2),
        borderRadius: theme.spacing(1.5),
        border: "1px solid rgba(244, 67, 54, 0.3)",
        backgroundColor: "rgba(244, 67, 54, 0.06)",
        color: "#c62828",
        marginBottom: theme.spacing(2),
    },
    loadingBox: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: theme.spacing(2),
        padding: theme.spacing(6),
    },
}));

const formatLabel = (key: string) =>
    key
        .replace(/_/g, " ")
        .replace(/\bid\b/gi, "ID")
        .replace(/\b\w/g, (c) => c.toUpperCase());

const formatValue = (key: string, value: unknown): string => {
    if (value === null || value === undefined || value === "") {
        return "—";
    }
    if (typeof value === "boolean") {
        return value ? "Yes" : "No";
    }
    if (key === "amount" && (typeof value === "number" || !isNaN(Number(value)))) {
        return `£${Number(value).toFixed(2)}`;
    }
    if (
        (key.includes("date") || key.includes("_at")) &&
        typeof value === "string" &&
        !Number.isNaN(Date.parse(value))
    ) {
        return new Date(value).toLocaleString();
    }
    return String(value);
};

const getItemColumns = (items: Record<string, unknown>[]) => {
    if (!items.length) {
        return ["id", "amount"];
    }
    const keys = new Set<string>();
    items.forEach((item) => Object.keys(item).forEach((k) => keys.add(k)));
    const preferred = ["id", "amount", "status", "transaction_status", "created_at", "updated_at"];
    const ordered = preferred.filter((k) => keys.has(k));
    const rest = Array.from(keys).filter((k) => !ordered.includes(k)).sort();
    return [...ordered, ...rest];
};

type UserInflightDetailsTabProps = {
    data: InflightDetailsPayload | null;
    loading: boolean;
    error: string | null;
    lookupValue: string | number | null;
    hasSearched: boolean;
};

const UserInflightDetailsTab = ({
    data,
    loading,
    error,
    lookupValue,
    hasSearched,
}: UserInflightDetailsTabProps) => {
    const classes = useStyles();
    const [expanded, setExpanded] = useState<string[]>([]);

    const sectionsWithItems = useMemo(() => {
        if (!data?.details) {
            return [];
        }
        return SECTIONS.map((section) => ({
            ...section,
            items: data.details?.[section.key]?.items || [],
            count: data.meta?.counts?.[section.key] ?? 0,
            total: data.meta?.totals?.[section.totalKey] ?? 0,
        }));
    }, [data]);

    const totalItems = useMemo(
        () => sectionsWithItems.reduce((sum, s) => sum + s.count, 0),
        [sectionsWithItems]
    );

    const handleAccordionChange =
        (panel: string) => (_: React.ChangeEvent<{}>, isExpanded: boolean) => {
            setExpanded((prev) =>
                isExpanded ? [...prev, panel] : prev.filter((p) => p !== panel)
            );
        };

    if (loading) {
        return (
            <div className={classes.root}>
                <div className={classes.loadingBox}>
                    <CircularProgress size={32} />
                    <Typography color="textSecondary">Loading inflight details…</Typography>
                </div>
            </div>
        );
    }

    if (!hasSearched) {
        return (
            <div className={classes.root}>
                <Paper className={classes.emptyState} elevation={0}>
                    <SearchIcon style={{ fontSize: 48, opacity: 0.35, marginBottom: 8 }} />
                    <Typography variant="h6" gutterBottom>
                        Inflight reward breakdown
                    </Typography>
                    <Typography variant="body2">
                        Enter an email or user ID above and click Fetch User Details to load
                        pending inflight rewards by category.
                    </Typography>
                </Paper>
            </div>
        );
    }

    if (error) {
        return (
            <div className={classes.root}>
                <div className={classes.errorBox}>
                    <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                        Could not load inflight details
                    </Typography>
                    <Typography variant="body2">{error}</Typography>
                </div>
            </div>
        );
    }

    if (!data) {
        return null;
    }

    const totalInflight = data.meta?.totals?.total_amount_inflight ?? 0;
    const referralCombined = data.meta?.totals?.referral_combined ?? 0;

    return (
        <div className={classes.root}>
            <Paper className={classes.hero} elevation={0}>
                <div className={classes.heroTop}>
                    <Box>
                        <Typography variant="overline" color="textSecondary">
                            User inflight overview
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Lookup: <strong>{lookupValue}</strong>
                            {data.user_id ? ` · User ID ${data.user_id}` : ""}
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <AccountBalanceWalletIcon color="primary" />
                        <Box>
                            <Typography variant="caption" color="textSecondary">
                                Total inflight
                            </Typography>
                            <Typography className={classes.heroAmount}>
                                £{Number(totalInflight).toFixed(2)}
                            </Typography>
                        </Box>
                    </Box>
                </div>
                <div className={classes.chipRow}>
                    <Chip
                        size="small"
                        label={`${totalItems} pending item${totalItems === 1 ? "" : "s"}`}
                        className={classes.summaryChip}
                    />
                    {referralCombined > 0 && (
                        <Chip
                            size="small"
                            color="primary"
                            variant="outlined"
                            label={`Referrals combined: £${Number(referralCombined).toFixed(2)}`}
                            className={classes.summaryChip}
                        />
                    )}
                </div>
            </Paper>

            {sectionsWithItems.map((section) => {
                const panelId = section.key;
                const columns = getItemColumns(section.items as Record<string, unknown>[]);

                return (
                    <Accordion
                        key={panelId}
                        className={classes.accordion}
                        expanded={expanded.includes(panelId)}
                        onChange={handleAccordionChange(panelId)}
                    >
                        <AccordionSummary
                            className={classes.accordionSummary}
                            expandIcon={<ExpandMoreIcon />}
                        >
                            <Typography className={classes.sectionTitle}>
                                {section.icon}
                                {section.label}
                            </Typography>
                            <div className={classes.sectionMeta}>
                                <Chip
                                    size="small"
                                    label={`${section.count} item${section.count === 1 ? "" : "s"}`}
                                />
                                <Chip
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    label={`£${Number(section.total).toFixed(2)}`}
                                />
                            </div>
                        </AccordionSummary>
                        <AccordionDetails>
                            {!section.items.length ? (
                                <Typography className={classes.emptySection}>
                                    No pending inflight {section.label.toLowerCase()} rewards for
                                    this user.
                                </Typography>
                            ) : (
                                <div className={classes.tableWrap}>
                                    <Table size="small" className={classes.table}>
                                        <TableHead>
                                            <TableRow>
                                                {columns.map((col) => (
                                                    <TableCell
                                                        key={col}
                                                        className={classes.headCell}
                                                    >
                                                        {formatLabel(col)}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {section.items.map((item, rowIndex) => (
                                                <TableRow key={`${section.key}-${rowIndex}`}>
                                                    {columns.map((col) => (
                                                        <TableCell
                                                            key={col}
                                                            className={
                                                                col === "amount"
                                                                    ? `${classes.bodyCell} ${classes.amountCell}`
                                                                    : classes.bodyCell
                                                            }
                                                        >
                                                            {formatValue(
                                                                col,
                                                                (item as Record<string, unknown>)[col]
                                                            )}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </div>
    );
};

export default UserInflightDetailsTab;
